import { formatMoney, TAX_MODE_LABELS, type TaxMode } from '@mt/shared';
import { useState } from 'react';
import { Pencil } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Dialog } from '@/components/ui/dialog';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { cn } from '@/lib/utils';
import { monthLabel, useMyPayslip, useMyPayslips, type InsuranceDetail, type LineItemView } from './payroll-api';

/** Phiếu lương chi tiết của 1 NV (dùng ở bảng lương và "Hồ sơ của tôi"). */
export interface PayslipData {
  employeeCode: string;
  fullName: string;
  departmentName: string | null;
  asiaStaffCode: string | null;
  stdDays: number;
  workDays: number;
  otHours: number;
  unexcusedDays: number;
  contractSalary: number;
  insuranceSalary: number;
  dependants: number;
  taxMode: TaxMode;
  gross: number;
  taxExempt: number;
  insuranceEmployee: number;
  taxableIncome: number;
  pit: number;
  deductions: number;
  net: number;
  insuranceEmployer?: number;
  employerCost?: number;
  items: LineItemView[];
  insurance?: InsuranceDetail | null;
}

const Row = ({ label, value, strong, muted }: { label: string; value: string; strong?: boolean; muted?: boolean }) => (
  <div className={cn('flex justify-between gap-3 py-1', strong && 'font-semibold', muted && 'text-xs text-muted-foreground')}>
    <span>{label}</span>
    <span className="tabular-nums">{value}</span>
  </div>
);

export function PayslipBody({ p, showEmployer }: { p: PayslipData; showEmployer?: boolean }) {
  const earnings = p.items.filter((i) => i.kind === 'earning');
  const deductions = p.items.filter((i) => i.kind === 'deduction' && i.autoSource !== 'insurance');
  const item = (i: LineItemView) => (
    <div key={i.code} className="flex justify-between gap-3 py-1">
      <span className="min-w-0">
        {i.name}
        {i.qty !== null && i.calc !== 'formula' && (
          <span className="ml-1 text-xs text-muted-foreground">
            ({i.qty.toLocaleString('vi-VN')}{i.autoSource === 'sunday' ? ' công Chủ nhật' : i.calc === 'by_hour' ? ' giờ' : ' công'}
            {i.calc === 'by_hour' && i.rate ? ` × ${formatMoney(i.rate)} đ` : ''})
          </span>
        )}
        {i.overrideAmount !== null && (
          <span className="ml-1 inline-flex items-center gap-0.5 text-xs text-warning-foreground" title={i.overrideReason ?? ''}>
            <Pencil className="size-3" />sửa tay
          </span>
        )}
      </span>
      <span className="tabular-nums">{formatMoney(i.amount)}</span>
    </div>
  );
  return (
    <div className="space-y-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">{p.fullName}</span>
        <span className="text-xs text-muted-foreground">{p.employeeCode}</span>
        {p.asiaStaffCode ? <Badge variant="info">{p.asiaStaffCode}</Badge> : <Badge variant="warning">Chưa mã Asia</Badge>}
        {p.departmentName && <span className="text-xs text-muted-foreground">· {p.departmentName}</span>}
      </div>
      <div className="grid grid-cols-2 gap-x-4 rounded-md border p-2 text-xs sm:grid-cols-4">
        <div><span className="text-muted-foreground">Công</span> <b className="tabular-nums">{p.workDays.toLocaleString('vi-VN')}/{p.stdDays.toLocaleString('vi-VN')}</b></div>
        <div><span className="text-muted-foreground">Giờ TC</span> <b className="tabular-nums">{p.otHours.toLocaleString('vi-VN')}</b></div>
        <div><span className="text-muted-foreground">Vắng KP</span> <b className="tabular-nums">{p.unexcusedDays.toLocaleString('vi-VN')}</b></div>
        <div><span className="text-muted-foreground">Thuế</span> <b>{TAX_MODE_LABELS[p.taxMode]}</b></div>
      </div>
      <section>
        <h3 className="border-b pb-1 text-xs font-semibold uppercase text-muted-foreground">Thu nhập</h3>
        {earnings.map(item)}
        <Row label="Tổng thu nhập (gross)" value={formatMoney(p.gross)} strong />
      </section>
      <section>
        <h3 className="border-b pb-1 text-xs font-semibold uppercase text-muted-foreground">Khấu trừ</h3>
        <Row label={`BHXH-BHYT-BHTN (lương đóng BH ${formatMoney(p.insuranceSalary)})`} value={formatMoney(p.insuranceEmployee)} />
        <Row label="Thuế TNCN" value={formatMoney(p.pit)} />
        <Row label={`Thu nhập tính thuế (miễn thuế ${formatMoney(p.taxExempt)}, ${p.dependants} người phụ thuộc)`} value={formatMoney(p.taxableIncome)} muted />
        {deductions.map(item)}
      </section>
      <div className={cn('flex items-center justify-between rounded-md px-3 py-2', p.net < 0 ? 'bg-error-soft text-error-foreground' : 'bg-success-soft text-success-foreground')}>
        <span className="font-semibold">Thực lĩnh</span>
        <span className="text-lg font-bold tabular-nums">{formatMoney(p.net)}</span>
      </div>
      {showEmployer && p.insurance && (
        <div className="text-xs text-muted-foreground">
          Công ty đóng: BHXH {formatMoney(p.insurance.employer.social)} · BHYT {formatMoney(p.insurance.employer.health)} · BHTN {formatMoney(p.insurance.employer.unemployment)} · KPCĐ{' '}
          {formatMoney(p.insurance.employer.union)} → chi phí DN {formatMoney(p.employerCost ?? 0)}
        </div>
      )}
    </div>
  );
}

/** "Hồ sơ của tôi": phiếu lương các kỳ đã duyệt (NV chỉ thấy của mình). */
export function MyPayslips() {
  const list = useMyPayslips();
  const [open, setOpen] = useState<number | null>(null);
  const slip = useMyPayslip(open);
  if (list.isLoading) return <LoadingState rows={2} />;
  if (list.error) return <ErrorState error={list.error} />;
  return (
    <section className="space-y-1" data-testid="my-payslips">
      <h3 className="text-sm font-semibold">Phiếu lương</h3>
      {!list.data?.length ? (
        <p className="text-xs text-muted-foreground">Chưa có phiếu lương (chỉ hiện kỳ đã được GĐ duyệt).</p>
      ) : (
        <ul className="divide-y rounded-md border text-sm">
          {list.data.map((r) => (
            <li key={r.id}>
              <button type="button" className="flex w-full justify-between px-3 py-2 text-left hover:bg-muted" onClick={() => setOpen(r.id)}>
                <span>Tháng {monthLabel(r.month)}</span>
                <span className="font-medium tabular-nums">{formatMoney(r.net)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {open !== null && (
        <Dialog open onOpenChange={(o) => !o && setOpen(null)} title={slip.data ? `Phiếu lương tháng ${monthLabel(slip.data.month)}` : 'Phiếu lương'}>
          {slip.isLoading ? <LoadingState /> : slip.error ? <ErrorState error={slip.error} /> : slip.data ? <PayslipBody p={slip.data} /> : <EmptyState title="Không có dữ liệu" />}
        </Dialog>
      )}
    </section>
  );
}
